"use client";

import { CurvedRow } from "@/components/feature/curved-row";
import { GameCard } from "@/components/feature/game-card";
import { GameRow } from "@/components/feature/game-row";
import { RiveTile } from "@/components/feature/rive-tile";
import { ShowcaseGrid } from "@/components/feature/showcase-grid";
import { type ContentOrderGameRow, organizeContentOrders } from "@/lib/content-orders";
import {
  readGameSectionCenterWhenSparse,
  readGameSectionCurveDirection,
  readGameSectionEnlargeMiddle,
  readGameSectionLayout,
  readGameSectionShowHeader,
  readGameSectionSidePadding,
  sectionToShowcaseItems,
} from "@/lib/game-sections";
import type { GameRow as GameRowType } from "@/lib/games";
import type { PageOriginContext } from "@/lib/page-origin-context";
import { isMobileDevice } from "@/lib/utils/device-detection";
import type { ContentOrderWithGames } from "@/types/content-order";
import type { ShowcaseGridDisplayItem } from "@/types/showcase-grid";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";

interface GamesRowsProps {
  contentOrders?: ContentOrderWithGames[];
  emptyMessage?: string;
  emptyDescription?: string;
  pageOrigin?: PageOriginContext | null;
}

/** Same tile box as `GameRow`: one size, then the row scrolls. */
/** Image and Rive tiles are shown whole: contained, unrounded, and free to reach past the box. */
/** Game tiles stay overflow-visible so the NEW badge can sit past the corner. */
const ROW_TILE_SIZE_CLASS = [
  "h-[26vw] w-[26vw] shrink-0",
  "sm:h-44 sm:w-44 md:h-52 md:w-52 lg:h-56 lg:w-56",
].join(" ");
const ROW_GAME_TILE_CLASS = ROW_TILE_SIZE_CLASS;

function MixedSectionRow({
  row,
  pageOrigin,
}: {
  row: RenderedSection;
  pageOrigin?: PageOriginContext | null;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canHover, setCanHover] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateEnds = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    setCanScrollLeft(scroller.scrollLeft > 4);
    setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 4);
  };

  useEffect(() => {
    const hoverQuery = window.matchMedia("(hover: hover)");
    const apply = () => setCanHover(hoverQuery.matches);
    apply();
    hoverQuery.addEventListener("change", apply);
    return () => hoverQuery.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    updateEnds();
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const observer = new ResizeObserver(updateEnds);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [row.showcaseItems.length]);

  const scrollByTile = (direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    const tile = scroller?.querySelector("[data-testid='game-section-row-tile']");
    const width = tile instanceof HTMLElement ? tile.offsetWidth + 16 : 240;
    scroller?.scrollBy({ left: direction * width, behavior: "smooth" });
  };

  return (
    <section className="group/row relative" data-testid="game-section-row">
      {row.showHeader ? (
        <h3 className="mb-3 px-1 text-xl font-bold text-white">{row.tag.name}</h3>
      ) : null}
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto px-3 pt-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="game-section-row-scroller"
        onScroll={updateEnds}
      >
        {row.showcaseItems.map((item) => (
          <SectionTile key={item.id} item={item} pageOrigin={pageOrigin} />
        ))}
      </div>
      {canScrollLeft && canHover ? (
        <button
          type="button"
          onClick={() => scrollByTile(-1)}
          className="pointer-events-none absolute top-1/2 left-0 z-20 flex h-20 w-10 -translate-y-1/2 items-center justify-center rounded-r-full bg-black/60 text-white opacity-0 transition-opacity group-hover/row:pointer-events-auto group-hover/row:opacity-100 hover:bg-black/80 md:h-24 md:w-12"
          aria-label="Previous games"
          data-testid="game-row-left-arrow"
        >
          <CaretLeft weight="bold" className="h-6 w-6" />
        </button>
      ) : null}
      {canScrollRight && canHover ? (
        <button
          type="button"
          onClick={() => scrollByTile(1)}
          className="pointer-events-none absolute top-1/2 right-0 z-20 flex h-20 w-10 -translate-y-1/2 items-center justify-center rounded-l-full bg-black/60 text-white opacity-0 transition-opacity group-hover/row:pointer-events-auto group-hover/row:opacity-100 hover:bg-black/80 md:h-24 md:w-12"
          aria-label="Next games"
          data-testid="game-row-right-arrow"
        >
          <CaretRight weight="bold" className="h-6 w-6" />
        </button>
      ) : null}
    </section>
  );
}

function SectionTile({
  item,
  pageOrigin,
}: {
  item: ShowcaseGridDisplayItem;
  pageOrigin?: PageOriginContext | null;
}) {
  return (
    <div
      className={item.type === "game" ? ROW_GAME_TILE_CLASS : ROW_TILE_SIZE_CLASS}
      data-testid="game-section-row-tile"
    >
      {item.type === "game" ? (
        <GameCard
          game={item.game}
          pageOrigin={pageOrigin}
          linkPrefetch={false}
          previewIdOverride={item.id}
        />
      ) : item.type === "rive" ? (
        <RiveTile src={item.assetUrl} className="h-full w-full" ariaLabel="" />
      ) : (
        // User-supplied section art.
        // eslint-disable-next-line @next/next/no-img-element
        <img src={item.assetUrl} alt="" className="h-full w-full object-contain" />
      )}
    </div>
  );
}

type RenderedSection = GameRowType & {
  _orderId?: string;
  layout: "row" | "grid" | "curved_row";
  showHeader: boolean;
  centerWhenSparse: boolean;
  sidePadding: number;
  curveDirection: "up" | "down";
  enlargeMiddle: boolean;
  showcaseItems: ShowcaseGridDisplayItem[];
};

function GameSectionView({
  row,
  emptyMessage,
  emptyDescription,
  pageOrigin,
}: {
  row: RenderedSection;
  emptyMessage: string;
  emptyDescription: string;
  pageOrigin?: PageOriginContext | null;
}) {
  const rowKey = row._orderId ? `content-order-${row._orderId}` : row.tag.id;
  const previewRowScope = row._orderId ?? `tag-${row.tag.id}`;
  if (row.layout === "grid") {
    return (
      <section key={rowKey} data-testid="game-section-grid">
        {row.showHeader ? (
          <h3 className="mb-3 px-1 text-xl font-bold text-white">{row.tag.name}</h3>
        ) : null}
        <ShowcaseGrid
          items={row.showcaseItems}
          emptyMessage={emptyMessage}
          emptyDescription={emptyDescription}
          pageOrigin={pageOrigin}
          linkPrefetch={false}
          centerWhenSparse={row.centerWhenSparse}
          sidePadding={row.sidePadding}
        />
      </section>
    );
  }
  if (row.layout === "curved_row") {
    return (
      <section key={rowKey} data-testid="game-section-curved">
        {row.showHeader ? (
          <h3 className="mb-3 px-1 text-xl font-bold text-white">{row.tag.name}</h3>
        ) : null}
        <CurvedRow
          items={row.showcaseItems}
          direction={row.curveDirection}
          enlargeMiddle={row.enlargeMiddle}
          sidePadding={row.sidePadding}
          pageOrigin={pageOrigin}
        />
      </section>
    );
  }
  if (row.showcaseItems.some((item) => item.type !== "game")) {
    return <MixedSectionRow row={row} pageOrigin={pageOrigin} />;
  }
  return (
    <div key={rowKey} data-testid="game-section-row">
      {row.games.length > 0 ? (
        <GameRow
          tag={row.tag}
          games={row.games}
          previewRowScope={previewRowScope}
          pageOrigin={pageOrigin}
          showHeader={row.showHeader}
        />
      ) : row.showHeader ? (
        <h3 className="px-1 text-xl font-bold text-white">{row.tag.name}</h3>
      ) : null}
    </div>
  );
}

export function GamesRows({
  contentOrders,
  emptyMessage = "No Rows Available",
  emptyDescription = "No games are organized in rows yet.",
  pageOrigin,
}: GamesRowsProps) {
  const isMobile = useMemo(() => isMobileDevice(), []);

  const gameRows = useMemo(() => {
    if (contentOrders && contentOrders.length > 0) {
      const organized = organizeContentOrders(contentOrders);
      return organized
        .map((orderRow: ContentOrderGameRow, index: number) => {
          let rowId: number;
          const orderId = orderRow.order.id;
          if (orderId.startsWith("temp-")) {
            rowId = -(index + 1);
          } else {
            const uuid = orderId.replace(/-/g, "");
            const firstPart = Number.parseInt(uuid.slice(0, 8), 16);
            const lastPart = Number.parseInt(uuid.slice(-8), 16);
            rowId = (firstPart ^ lastPart) + 2000000;
          }

          const rowGames = orderRow.games.filter((g) => (isMobile ? !g.desktop_only : true));
          const elements = orderRow.order.elements?.filter((element) => {
            if (element.content_type !== "game" || !element.game) return true;
            return isMobile ? !element.game.desktop_only : true;
          });
          const showcaseItems = sectionToShowcaseItems({
            id: orderRow.order.id,
            name: orderRow.order.name,
            description: orderRow.order.description,
            display_order: orderRow.order.display_order,
            active: true,
            created_at: "",
            updated_at: "",
            layout: orderRow.order.layout,
            show_header: orderRow.order.show_header,
            center_when_sparse: orderRow.order.center_when_sparse,
            side_padding: orderRow.order.side_padding,
            curve_direction: orderRow.order.curve_direction,
            enlarge_middle: orderRow.order.enlarge_middle,
            grid_slots: orderRow.order.grid_slots,
            elements,
            games: rowGames.map((game, gameIndex) => ({
              id: gameIndex,
              content_order_id: orderRow.order.id,
              game_id: game.id,
              display_order: gameIndex,
              created_at: "",
              updated_at: "",
              game,
            })),
          });

          return {
            tag: {
              id: rowId,
              name: orderRow.order.name,
              slug: orderRow.order.slug,
              description: orderRow.order.description,
              display_order: orderRow.order.display_order,
            },
            games: rowGames,
            layout: readGameSectionLayout(orderRow.order.layout),
            showHeader: readGameSectionShowHeader(orderRow.order.show_header),
            centerWhenSparse: readGameSectionCenterWhenSparse(orderRow.order.center_when_sparse),
            sidePadding: readGameSectionSidePadding(orderRow.order.side_padding),
            curveDirection: readGameSectionCurveDirection(orderRow.order.curve_direction),
            enlargeMiddle: readGameSectionEnlargeMiddle(orderRow.order.enlarge_middle),
            showcaseItems,
            _orderId: orderId,
          };
        })
        .filter((row) => row.games.length > 0 || row.showcaseItems.length > 0);
    }

    return [];
  }, [contentOrders, isMobile]);

  if (gameRows.length === 0) {
    return (
      <div className="bg-white/10 p-8 rounded-2xl text-center">
        <h2 className="text-xl font-bold mb-2">{emptyMessage}</h2>
        <p>{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {gameRows.map((row) => (
        <GameSectionView
          key={row._orderId ? `content-order-${row._orderId}` : row.tag.id}
          row={row}
          emptyMessage={emptyMessage}
          emptyDescription={emptyDescription}
          pageOrigin={pageOrigin}
        />
      ))}
    </div>
  );
}
